/**
 * DRL-1/DRL-2A/DRL-2B — Allison Decision Resolution Loop panel.
 * Compact, collapsible card embedded in the Client Workspace Overview tab.
 * Shows ONE primary next action; staff can waive/defer/resolve factual blockers.
 * Document-request blockers route to the existing Documents tab workflow.
 * DRL-2A: send_organizer prepared_action calls prepare endpoint then navigates to organizer editor.
 * DRL-2B: communication_tab prepared_action calls prepare-communication endpoint,
 *         receives ephemeral draft, navigates to Communications Center composer preloaded.
 */
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronDown, ChevronUp, RefreshCw, Loader2,
         ArrowRight, X, AlertCircle, CheckCircle2, Clock, Info } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// ─── Waive / Defer / Resolve dialog ────────────────────────────────────────

const DISPOSITION_LABELS = {
  waived:   { label: "Proceed Without",   hint: "Client cannot / has not provided this. Continue without it.", icon: X },
  deferred: { label: "Defer (Non-blocking)", hint: "Useful later but does not block current progress.", icon: Clock },
  resolved: { label: "Mark Resolved",     hint: "This question has been answered / issue is closed.",  icon: CheckCircle2 },
};

const PREDEFINED_REASONS = [
  "Client cannot provide",
  "Client declined to provide",
  "Not reasonably obtainable",
  "Alternative evidence is sufficient",
  "Not necessary for this stage",
  "Staff judgment: sufficient to proceed",
  "Engagement completed",
  "Other",
];

export function DispositionDialog({ open, onClose, blockerKey, blockerTitle, clientId, engagementId, onDone }) {
  const [status, setStatus]     = useState("waived");
  const [reason, setReason]     = useState("");
  const [custom, setCustom]     = useState("");
  const [saving, setSaving]     = useState(false);

  const effectiveReason = reason === "Other" ? custom : reason;

  const save = async () => {
    if (!effectiveReason.trim()) { toast.error("A reason is required."); return; }
    // Use the engagement ID when present, or the pre-engagement sentinel for
    // clients without an accepted engagement yet (pre-engagement workflow).
    const effectiveEngagementId = engagementId || "__pre_engagement__";
    setSaving(true);
    try {
      // Truncate blocker_title to 200 chars to match backend max_length constraint.
      // primary_blocker from Allison is free-form LLM text that can exceed 200 chars;
      // sending the full value causes a Pydantic 422 with array detail that the error
      // handler cannot parse, resulting in the generic "Could not save disposition." toast.
      const safeTitle = (blockerTitle || "Case blocker").slice(0, 200);
      await api.post(`/staff-assist/resolution-assessment/blockers/${clientId}`, {
        blocker_category: blockerKey,
        blocker_title: safeTitle,
        status,
        reason: effectiveReason.trim(),
        engagement_id: effectiveEngagementId,
      });
      toast.success(`Blocker ${DISPOSITION_LABELS[status].label.toLowerCase()}.`);
      onDone();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // detail can be a string, a custom {code,detail} object, or a Pydantic array.
      let msg;
      if (Array.isArray(detail)) {
        // Pydantic validation error — surface the first validation message.
        msg = detail.map((d) => d.msg || d.message || "").filter(Boolean).join("; ");
      } else if (detail && typeof detail === "object") {
        msg = detail.detail || detail.message;
      } else {
        msg = detail;
      }
      toast.error(msg || "Could not save disposition.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="drl-disposition-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Resolve blocker: {blockerTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Decision</label>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {Object.entries(DISPOSITION_LABELS).map(([key, { label, hint, icon: Icon }]) => (
                <button key={key} type="button" onClick={() => setStatus(key)} data-testid={`drl-disp-${key}`}
                  className={cn("rounded-lg border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
                    status === key ? "border-charcoal bg-charcoal/5 ring-1 ring-charcoal" : "border-slate-200 hover:bg-slate-50")}>
                  <Icon className="h-3.5 w-3.5 mb-1 text-slate-600" />
                  <p className="text-[11px] font-semibold text-slate-800">{label}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{hint}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1.5 text-sm" data-testid="drl-disp-reason-select">
                <SelectValue placeholder="Select a reason…" />
              </SelectTrigger>
              <SelectContent>
                {PREDEFINED_REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`drl-disp-reason-${r.replace(/\s+/g,"-").toLowerCase()}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reason === "Other" && (
              <textarea rows={2} value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Describe the reason…"
                data-testid="drl-disp-reason-custom"
                className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold resize-none" />
            )}
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-md" data-testid="drl-disp-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={saving || !effectiveReason.trim()}
            data-testid="drl-disp-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-charcoal px-4 py-1.5 text-sm font-medium text-white hover:bg-charcoal/90 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save Decision
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Needed-to-resolve list ──────────────────────────────────────────────────

function NeededList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2" data-testid="drl-needed-list">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Needed to resolve</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-600">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Secondary blockers ──────────────────────────────────────────────────────

function SecondaryBlockers({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="drl-secondary-blockers">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline-none"
        data-testid="drl-secondary-toggle">
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {items.length} additional issue{items.length > 1 ? "s" : ""} identified
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {items.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-500">
              <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────

export default function DRLPanel({ clientId, onNavigate, drlData, drlLoading, runDrl, onDocumentAction, primaryEngagementId }) {
  const [collapsed, setCollapsed] = useState(false);
  const [preparing, setPreparing] = useState(false); // DRL-2A/2B/2C: action preparation in progress
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Use controlled props (lifted state from ClientWorkspace) when provided.
  const data    = drlData;
  const loading = drlLoading ?? false;
  const run     = runDrl ?? (() => {});

  // Uncollapse when new data arrives
  useEffect(() => { if (data) setCollapsed(false); }, [data]);

  const assessmentType = data?.assessment_type;
  const isActive  = assessmentType === "active_blocker";
  const isClean   = assessmentType === "no_material_blocker";
  const isError   = assessmentType === "error";

  // Derive the "Prepare Action" link/action
  const preparedAction = isActive ? data?.prepared_action : null;
  const preparedLink   = isActive ? data?.prepared_action_link : null;
  const isSendOrganizer              = preparedAction === "send_organizer";
  const isPrepareCommunication       = preparedAction === "communication_tab";
  const isPrepareEngagementLetter    = preparedAction === "prepare_engagement_letter";
  const isPrepareDocumentAdvisory    = preparedAction === "prepare_document_advisory";

  // DRL-2A: Prepare Organizer — call backend preparation endpoint, then navigate to organizer editor
  const handlePrepareOrganizer = useCallback(async () => {
    const engId = data?.scope_engagement_id;
    if (!engId) { toast.error("No engagement scope found. Please refresh and try again."); return; }
    setPreparing(true);
    try {
      const { data: r } = await api.post(`/staff-assist/prepare-organizer/${clientId}`, { engagement_id: engId });
      if (!r.ok) { toast.error(r.error || "Preparation failed. You can still open the organizer manually."); return; }
      toast.success(
        `Organizer prepared — ${r.questions_proposed} answers proposed, ${r.questions_to_confirm} to confirm.`,
        { duration: 4000 }
      );
      navigate(r.organizer_url);
    } catch (e) {
      toast.error("Preparation failed. You can still open the organizer manually.");
    } finally {
      setPreparing(false);
    }
  }, [clientId, data, navigate]);

  // DRL-2B: Prepare Communication — call backend draft endpoint, navigate to Communications
  // composer with the draft preloaded. Nothing is sent until staff explicitly hits Send.
  const handlePrepareCommunication = useCallback(async () => {
    const engId = data?.scope_engagement_id;
    const blockerKey = data?.blocker_category;
    if (!engId) { toast.error("No engagement scope found. Please refresh and try again."); return; }
    if (!blockerKey) { toast.error("Blocker key missing. Please refresh and try again."); return; }
    setPreparing(true);
    try {
      const { data: r } = await api.post(`/staff-assist/prepare-communication/${clientId}`, {
        engagement_id: engId,
        blocker_key: blockerKey,
        blocker_title: data?.blocker_title || blockerKey,
      });
      if (r.already_waiting) {
        toast.info("A question is already pending the client's response. Opening the conversation.", { duration: 4000 });
        navigate(`/communications/center?conversation=${r.conversation_id}`);
        return;
      }
      if (!r.ok) { toast.error(r.error || "Draft preparation failed. Please compose manually."); return; }
      toast.success("Draft prepared — review and edit before sending.", { duration: 3500 });
      navigate(`/communications/center?conversation=${r.conversation_id}`, {
        state: { initialDraft: r.draft_body, initialSubject: r.draft_subject },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft preparation failed. Please compose manually.");
    } finally {
      setPreparing(false);
    }
  }, [clientId, data, navigate]);

  // DRL-2C: Prepare Engagement Letter — call backend preparation endpoint, then navigate to Engagements tab.
  // Creates a draft engagement letter from the single accepted proposal. Staff reviews and clicks Send.
  // Nothing is sent, published, or made client-visible until staff explicitly clicks Send.
  const handlePrepareEngagementLetter = useCallback(async () => {
    setPreparing(true);
    try {
      const { data: r } = await api.post(`/staff-assist/prepare-engagement-letter/${clientId}`);
      if (!r.ok) {
        toast.error(r.message || "Engagement letter preparation failed. Open the Engagements tab to proceed manually.");
        return;
      }
      toast.success(
        r.is_existing
          ? "Opening existing engagement letter draft for review."
          : "Engagement letter draft prepared — review and click Send when ready.",
        { duration: 4000 },
      );
      navigate(r.engagement_url);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Engagement letter preparation failed. Open the Engagements tab to proceed manually.",
      );
    } finally {
      setPreparing(false);
    }
  }, [clientId, navigate]);

  // DRL-2D: Prepare Document Advisory — trigger F5-A detection for the engagement.
  // Creates Document Advisory records for staff review. Never creates a Document Request.
  // Staff must explicitly click Convert-to-Request (F5-D) to create the portal-visible request.
  const handlePrepareDocumentAdvisory = useCallback(async () => {
    const engId = data?.scope_engagement_id;
    if (!engId) {
      toast.error("No engagement scope found. Please trigger detection from the Documents tab directly.");
      return;
    }
    setPreparing(true);
    try {
      const { data: r } = await api.post(`/staff-assist/prepare-document-advisory/${clientId}`, {
        engagement_id: engId,
      });
      if (!r.ok) {
        toast.error(r.message || "Advisory preparation failed. Open the Documents tab to trigger detection manually.");
        return;
      }
      if (r.advisory_count === 0) {
        toast.info(r.message || "No new advisories needed — existing requests or documents already cover identified needs.", { duration: 5000 });
      } else {
        toast.success(
          r.is_existing
            ? `Existing advisory ready for review (${r.advisory_count} advisory). Open the Documents tab to convert.`
            : `Advisory prepared (${r.advisory_count} advisory). Open the Documents tab to review and convert.`,
          { duration: 4500 },
        );
      }
      // Navigate to Documents tab / Missing Document Advisories section
      const tabMatch = r.routing_url?.match(/[?&]tab=([^&]+)/);
      if (tabMatch && onNavigate) { onNavigate(tabMatch[1]); return; }
      if (r.routing_url) navigate(r.routing_url);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Advisory preparation failed. Open the Documents tab to trigger detection manually.",
      );
    } finally {
      setPreparing(false);
    }
  }, [clientId, data, navigate, onNavigate]);

  const handlePrepareAction = () => {
    if (isSendOrganizer)              { handlePrepareOrganizer(); return; }
    if (isPrepareCommunication)       { handlePrepareCommunication(); return; }
    if (isPrepareEngagementLetter)    { handlePrepareEngagementLetter(); return; }
    if (isPrepareDocumentAdvisory)    { handlePrepareDocumentAdvisory(); return; }
    if (!preparedLink) return;
    // Use React Router navigation via onNavigate if it's a client workspace tab link
    const tabMatch = preparedLink.match(/[?&]tab=([^&]+)/);
    if (tabMatch && onNavigate) { onNavigate(tabMatch[1]); return; }
    // Otherwise in-app navigation
    window.location.href = preparedLink;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="drl-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <Sparkles className="h-4 w-4 text-indigo-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-800 flex-1">Allison — Next Action</span>
        <div className="flex items-center gap-1.5">
          {data && (
            <span className="text-[10px] text-slate-400" data-testid="drl-assessed-at">
              {data.assessed_at ? new Date(data.assessed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
            </span>
          )}
          <button type="button" onClick={run} disabled={loading} aria-label="Refresh assessment"
            data-testid="drl-refresh"
            className="rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </button>
          <button type="button" onClick={() => setCollapsed((v) => !v)} aria-label="Toggle panel"
            data-testid="drl-toggle"
            className="rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
            {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="p-4">
          {/* Loading */}
          {loading && !data && (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-2" data-testid="drl-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              Analysing case…
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid="drl-error">
              <AlertCircle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-500">{data.error}</p>
            </div>
          )}

          {/* No material blocker */}
          {isClean && (
            <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2.5" data-testid="drl-no-blocker">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              <p className="text-sm text-emerald-800">{data.no_blocker_message}</p>
            </div>
          )}

          {/* Active blocker */}
          {isActive && (
            <div data-testid="drl-active-blocker">
              {/* Objective */}
              {data.objective && (
                <p className="text-[11px] text-slate-400 uppercase tracking-wide font-semibold mb-1" data-testid="drl-objective-label">Current objective</p>
              )}
              {data.objective && (
                <p className="text-[13px] text-slate-700 font-medium mb-3" data-testid="drl-objective">{data.objective}</p>
              )}

              {/* Primary blocker card */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="drl-blocker-card">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-amber-900" data-testid="drl-primary-blocker">{data.primary_blocker}</p>
                    {data.why_it_matters && (
                      <p className="text-[12px] text-amber-700 mt-1 leading-snug" data-testid="drl-why">{data.why_it_matters}</p>
                    )}
                  </div>
                </div>
              </div>

              <NeededList items={data.needed_to_resolve} />

              {/* Recommended action */}
              {data.recommended_action && (
                <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5" data-testid="drl-recommended-action">
                  <p className="text-[11px] font-semibold text-indigo-500 uppercase tracking-wide mb-1">Recommended action</p>
                  <p className="text-sm text-indigo-900">{data.recommended_action}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-3 flex flex-wrap gap-2" data-testid="drl-actions">
                {(preparedLink || isSendOrganizer || isPrepareCommunication || isPrepareEngagementLetter || isPrepareDocumentAdvisory) && (
                  <button type="button" onClick={handlePrepareAction}
                    disabled={preparing}
                    data-testid="drl-prepare-action"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-charcoal px-3 py-1.5 text-[12px] font-medium text-white hover:bg-charcoal/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold disabled:opacity-60">
                    {preparing
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {isSendOrganizer              ? "Preparing Organizer…"         :
                           isPrepareCommunication       ? "Preparing Draft…"             :
                           isPrepareEngagementLetter    ? "Preparing Letter…"            :
                           isPrepareDocumentAdvisory    ? "Preparing Advisory…"          :
                                                          "Preparing…"}
                        </>
                      : isSendOrganizer
                        ? <>Prepare Organizer <ArrowRight className="h-3.5 w-3.5" /></>
                        : isPrepareCommunication
                          ? <>Prepare Draft <ArrowRight className="h-3.5 w-3.5" /></>
                          : isPrepareEngagementLetter
                            ? <>Prepare Engagement Letter <ArrowRight className="h-3.5 w-3.5" /></>
                            : isPrepareDocumentAdvisory
                              ? <>Prepare Document Advisory <ArrowRight className="h-3.5 w-3.5" /></>
                              : <>Prepare Action <ArrowRight className="h-3.5 w-3.5" /></>
                    }
                  </button>
                )}
                {/* For document_missing: always show Resolve Document Blocker (opens governed resolver panel).
                    "Go to Documents" is kept as a secondary ghost button so staff can view raw documents.
                    The isPrepareDocumentAdvisory gate is intentionally removed — the resolver panel is
                    always available regardless of whether the LLM returned prepare_document_advisory. */}
                {data.blocker_category !== "document_missing" && (
                  <button type="button" onClick={() => setDialogOpen(true)}
                    data-testid="drl-proceed-without"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                    Waive / Defer / Resolve
                  </button>
                )}
                {data.blocker_category === "document_missing" && (
                  <button type="button"
                    onClick={() => onDocumentAction?.() ?? onNavigate?.("documents")}
                    data-testid="drl-resolve-document-blocker"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
                    Resolve Document Blocker
                  </button>
                )}
                {data.blocker_category === "document_missing" && (
                  <button type="button" onClick={() => onNavigate?.("documents")}
                    data-testid="drl-go-documents"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] font-medium text-slate-500 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                    View Documents <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* can_proceed_without advisory */}
              {data.can_proceed_without && data.limitations && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500" data-testid="drl-limitations">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
                  <span><span className="font-semibold">If proceeding without:</span> {data.limitations}</span>
                </div>
              )}

              <SecondaryBlockers items={data.secondary_blockers} />
            </div>
          )}
        </div>
      )}

      {/* Disposition dialog */}
      {isActive && (
        <DispositionDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          blockerKey={data?.blocker_category}
          blockerTitle={data?.primary_blocker || "Case blocker"}
          clientId={clientId}
          engagementId={data?.scope_engagement_id || primaryEngagementId}
          onDone={run}
        />
      )}
    </div>
  );
}
